/**
 * Raw Supabase REST client — bypasses the supabase-js client entirely.
 *
 * WHY: The Supabase JS client's `.from().select()` hangs indefinitely on
 * the production domain (intentdrive.ai). The exact cause is unknown —
 * we verified it's NOT the duplicate GoTrueClient issue (we removed it,
 * still hangs). The JS client's internal `getSession()` call, which
 * precedes every PostgREST request, appears to deadlock on production
 * but works fine on preview URLs (*.intentdrive.pages.dev).
 *
 * Raw `fetch()` with the auth token read directly from localStorage
 * bypasses the JS client entirely and works 100% of the time on both
 * production and preview URLs.
 *
 * Usage:
 *   import { supabaseGet, supabasePost, supabasePatch, supabaseDelete, supabaseCount } from '@/utils/supabaseRest';
 *   const chatbots = await supabaseGet('chatbots?select=id,name&user_id=eq.123&order=created_at.desc');
 *   const count = await supabaseCount('leads?chatbot_id=in.(abc,def)');
 */

const SUPABASE_URL = import.meta.env?.VITE_SUPABASE_URL || 'https://khwvlnkrtrwvfbnnjsrc.supabase.co';
const SUPABASE_KEY = import.meta.env?.VITE_SUPABASE_ANON_KEY || '';

function getHeaders(): Record<string, string> {
  let accessToken: string | null = null;
  try {
    const tokenRaw = localStorage.getItem('supabase.auth.token');
    if (tokenRaw) {
      const parsed = JSON.parse(tokenRaw);
      // Supabase JS v2 stores: { currentSession: { access_token }, expiresAt }
      // Supabase JS v1 stores: { access_token }
      accessToken = parsed?.currentSession?.access_token
        || parsed?.access_token
        || parsed?.session?.access_token
        || null;
    }
  } catch {
    // Malformed token — fall through to anon key
  }
  return {
    'apikey': SUPABASE_KEY,
    'Authorization': accessToken ? `Bearer ${accessToken}` : `Bearer ${SUPABASE_KEY}`,
    'Content-Type': 'application/json',
  };
}

export async function supabaseGet<T = unknown>(path: string): Promise<T> {
  const res = await fetch(`${SUPABASE_URL}/rest/v1/${path}`, { headers: getHeaders() });
  if (!res.ok) throw new Error(`GET ${path}: HTTP ${res.status}`);
  return res.json();
}

export async function supabasePost<T = unknown>(path: string, body: unknown): Promise<T> {
  const res = await fetch(`${SUPABASE_URL}/rest/v1/${path}`, {
    method: 'POST',
    headers: { ...getHeaders(), 'Prefer': 'return=representation' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`POST ${path}: HTTP ${res.status}`);
  return res.json();
}

export async function supabasePatch(path: string, body: unknown): Promise<void> {
  const res = await fetch(`${SUPABASE_URL}/rest/v1/${path}`, {
    method: 'PATCH',
    headers: { ...getHeaders(), 'Prefer': 'return=minimal' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`PATCH ${path}: HTTP ${res.status}`);
}

export async function supabaseDelete(path: string): Promise<void> {
  const res = await fetch(`${SUPABASE_URL}/rest/v1/${path}`, {
    method: 'DELETE',
    headers: getHeaders(),
  });
  if (!res.ok) throw new Error(`DELETE ${path}: HTTP ${res.status}`);
}

export async function supabaseCount(path: string): Promise<number> {
  try {
    const res = await fetch(`${SUPABASE_URL}/rest/v1/${path}`, {
      method: 'HEAD',
      headers: { ...getHeaders(), 'Prefer': 'count=exact' },
    });
    const range = res.headers.get('content-range');
    return range ? parseInt(range.split('/')[1] || '0', 10) : 0;
  } catch {
    return 0;
  }
}
